import {
  forwardRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { ICellEditorParams } from 'ag-grid-community';

import type { OnFarmImplantQueueRecord } from '@/services/on-farm-implant';

export type UndoInputCellEditorParams = {
  inputType: 'text' | 'number' | 'date';
  maxLength?: number;
  min?: number;
  renderUndo: (
    row: OnFarmImplantQueueRecord,
    beforeRevert: () => void,
  ) => ReactNode;
};

type UndoInputCellEditorProps = ICellEditorParams<OnFarmImplantQueueRecord> &
  UndoInputCellEditorParams;

const isPrintableKey = (key: string | null | undefined): key is string =>
  !!key && key.length === 1;

const initialText = (
  value: unknown,
  eventKey: string | null | undefined,
  inputType: UndoInputCellEditorParams['inputType'],
): string => {
  if (inputType === 'date') {
    return value === null || value === undefined
      ? ''
      : String(value).slice(0, 10);
  }
  if (isPrintableKey(eventKey)) return eventKey;
  if (eventKey === 'Backspace' || eventKey === 'Delete') return '';
  return value === null || value === undefined ? '' : String(value);
};

export const UndoInputCellEditor = forwardRef(
  (props: UndoInputCellEditorProps, ref) => {
    const {
      value,
      eventKey,
      data,
      api,
      inputType,
      maxLength,
      min,
      renderUndo,
    } = props;
    const [text, setText] = useState(() =>
      initialText(value, eventKey, inputType),
    );
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      getValue: () => {
        if (inputType === 'text') return text;
        if (inputType === 'date') return text === '' ? null : text;
        if (text.trim() === '') return null;
        const numeric = Number(text);
        return Number.isFinite(numeric) ? Math.round(numeric) : null;
      },
    }));

    useEffect(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      if (inputType !== 'date' && !isPrintableKey(eventKey)) input.select();
    }, [eventKey, inputType]);

    return (
      <span className="flex h-full w-full items-center gap-1 px-2">
        <input
          ref={inputRef}
          type={inputType}
          value={text}
          maxLength={maxLength}
          min={min}
          step={inputType === 'number' ? 1 : undefined}
          onChange={(event) => setText(event.target.value)}
          className="h-full min-w-0 flex-1 [appearance:textfield] border-0 bg-transparent p-0 shadow-none outline-none focus:ring-0 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        {data && renderUndo(data, () => api.stopEditing(true))}
      </span>
    );
  },
);

UndoInputCellEditor.displayName = 'UndoInputCellEditor';
